'use client';

import { useEffect, useContext, memo } from 'react';

import { css } from '@emotion/react';
import { useMediaQuery } from '@mui/material';
import { AmplifyServiceContext, Types } from '@scorenco/core';

import { useHumanInteraction } from './useHumanInteraction';
import { useTextAd } from './useTextAd';
import { useImpressionPixel } from './useImpressionPixel';
import { trackAdClick } from './trackAdClick';

export interface TextAd {
  id: string;
  content: string;
  icon: string;
  display_type: string;
  campaign_url: string;
  position: string;
  sport_id?: string;
  department_id?: string;
  impression_pixel_url?: string;
  link_url?: string;
  logo_url?: string;
}

export interface TextAdState {
  text: TextAd | null;
  isLoading: boolean;
  error: boolean;
  isAvailable: boolean;
}

export interface TextAdsProps {
  sport_id?: Types.Clubs_Sport['id'];
  department_id?: Types.Places_Department['id'];
  style?: React.CSSProperties;
  display_type?: string;
  logged_in?: number;
  screen?: string;
  widget_id?: number;
  onStateChange?: (state: TextAdState) => void;
  impressionEnabled?: boolean;
}

declare global {
  interface Window {
    __isPremiumUser?: boolean;
  }
}

const isPremiumClient = (): boolean =>
  typeof window !== 'undefined' && window.__isPremiumUser === true;

const wrapperStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  gap: 8px;
  padding: 12px 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
  text-decoration: none;
  color: inherit;
  transition: background-color 0.2s ease;
  min-height: 48px;
  -webkit-tap-highlight-color: transparent;
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  pointer-events: auto;
`;

const handleHover = (e: React.MouseEvent, color: string) => {
  (e.currentTarget as HTMLElement).style.backgroundColor = color;
};

const TextAdsComponent = ({
  sport_id,
  department_id,
  style,
  display_type,
  logged_in = undefined,
  screen: screenProp,
  widget_id,
  onStateChange,
  impressionEnabled = true,
}: TextAdsProps) => {
  const amplifyContext = useContext(AmplifyServiceContext);
  const { user } =
    display_type === 'widget' || !amplifyContext
      ? { user: null }
      : amplifyContext;
  const isHumanInteraction = useHumanInteraction();

  const isMobile = useMediaQuery('(max-width:768px)');
  const screen =
    display_type === 'widget'
      ? ''
      : screenProp || (isMobile ? 'mobile' : 'desktop');

  const isLoggedIn =
    logged_in !== undefined ? logged_in : user?.info?.id ? 1 : 0;

  const { text, isLoading, error, isAvailable } = useTextAd({
    display_type,
    sport_id,
    department_id,
    isLoggedIn,
    screen,
    widgetId: widget_id,
  });

  useEffect(() => {
    onStateChange?.({ text, isLoading, error, isAvailable });
  }, [text, isLoading, error, isAvailable, onStateChange]);

  useImpressionPixel({
    enabled: isAvailable && impressionEnabled,
    pixelUrl: text?.impression_pixel_url,
  });

  if (!isAvailable || !text?.content) {
    return null;
  }

  const isWidgetScreen = display_type === 'widget' || !!widget_id;
  const link = text.link_url
    ? text.link_url
    : `https://${process.env.NEXT_PUBLIC_CACHE_ENDPOINT}/ads/text/${text.id}/link/`;

  const handleClick = (e: React.MouseEvent) => {
    if (
      e.detail === 0 ||
      e.screenX === 0 ||
      e.screenY === 0 ||
      !isHumanInteraction
    ) {
      e.preventDefault();
      return;
    }

    if (text.link_url) {
      trackAdClick(
        `https://${process.env.NEXT_PUBLIC_CACHE_ENDPOINT}/ads/text/${text.id}/link/`
      );
    }
  };

  const useLink = !(isWidgetScreen && !text.link_url);
  const Wrapper: React.ElementType = useLink ? 'a' : 'div';
  const linkProps = useLink
    ? {
        href: link,
        target: '_blank',
        rel: 'noopener noreferrer',
        onClick: handleClick,
      }
    : {};

  return (
    <div style={{ width: '100%', ...style }}>
      <Wrapper
        data-ad-type="text"
        data-ad-id={text.id}
        css={wrapperStyles}
        onMouseEnter={(e: React.MouseEvent) => handleHover(e, '#e9ecef')}
        onMouseLeave={(e: React.MouseEvent) => handleHover(e, '#f8f9fa')}
        onTouchStart={(e: React.TouchEvent) => {
          (e.currentTarget as HTMLElement).style.backgroundColor = '#e9ecef';
        }}
        onTouchEnd={(e: React.TouchEvent) => {
          (e.currentTarget as HTMLElement).style.backgroundColor = '#f8f9fa';
        }}
        {...linkProps}
      >
        {text.logo_url ? (
          <img
            src={text.logo_url}
            alt="logo"
            style={{
              width: 48,
              height: 48,
              objectFit: 'contain',
              flexShrink: 0,
            }}
          />
        ) : (
          text.icon && (
            <span
              style={{
                fontSize: '20px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px',
                lineHeight: '1',
              }}
            >
              {text.icon}
            </span>
          )
        )}
        <span
          style={{
            fontSize: '14px',
            lineHeight: '1.4',
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            whiteSpace: 'normal',
            justifyContent: 'center',
            textAlign: 'center',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
          }}
        >
          {text.content}
        </span>
      </Wrapper>
    </div>
  );
};

const TextAdsComponentWithoutPremium = (props: TextAdsProps) => {
  if (isPremiumClient()) {
    return null;
  }
  return <TextAdsComponent {...props} />;
};

export const TextAds = memo(TextAdsComponentWithoutPremium);
